import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Heading,
  Text,
  Stack,
  Container,
  Divider,
} from '@/components/ui/index.js';
import { useVenueStore } from '@/features/venues/store/useVenueStore.js';
import { ImageGallery } from './components/ImageGallery/index.js';
import { AmenityList } from './components/AmenityList/index.js';
import { ReviewCard } from './components/ReviewCard/index.js';
import { EventList } from './components/EventList/index.js';
import { QuickActions } from './components/QuickActions/index.js';
import { HeaderNav } from './components/HeaderNav/index.js';
import { SimilarVenues } from './components/SimilarVenues/index.js';
import styles from './VenueDetailPage.module.css';

/**
 * VenueDetailPage - Detailed venue view with photos, amenities,
 * upcoming events, reviews, and location info.
 * 
 * Route: /venues/:id
 */
export function VenueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVenueById, toggleSaved, isVenueSaved } = useVenueStore();
  
  const [venue, setVenue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  
  // Load venue data
  useEffect(() => {
    const loadVenue = async () => {
      setIsLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      const venueData = getVenueById(id);
      if (venueData) {
        setVenue(venueData);
        setIsSaved(isVenueSaved(id));
      }
      setIsLoading(false);
    };
    
    loadVenue();
  }, [id, getVenueById, isVenueSaved]);
  
  const handleBack = () => {
    navigate('/venues');
  };
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: venue?.name,
          text: `Check out ${venue?.name} on SportsSpots!`,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  
  const handleToggleSave = () => {
    const newSavedState = toggleSaved(id);
    setIsSaved(newSavedState);
  };
  
  const handleCall = () => {
    if (venue?.phone) {
      window.location.href = `tel:${venue.phone}`;
    }
  };
  
  const handleDirections = () => {
    if (venue?.coordinates) {
      const { lat, lng } = venue.coordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  };
  
  const handleWebsite = () => {
    if (venue?.website) {
      window.open(venue.website, '_blank');
    }
  };
  
  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };
  
  const handleViewAllEvents = () => {
    navigate('/events', { state: { venueId: id } });
  };
  
  const handleViewAllPhotos = () => {
    // Placeholder for lightbox
    console.log('View all photos clicked');
  };
  
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <Text color="secondary">Loading venue...</Text>
      </div>
    );
  }
  
  if (!venue) {
    return (
      <Container size="md" padding className={styles.errorContainer}>
        <Stack direction="column" gap={4} align="center">
          <Heading level={2} color="primary">Venue Not Found</Heading>
          <Text color="secondary">The venue you're looking for doesn't exist.</Text>
          <button 
            onClick={handleBack}
            className={styles.backButton}
          >
            Back to Venues
          </button>
        </Stack>
      </Container>
    );
  }
  
  // Check if venue is currently open
  const isOpen = venue.isOpen;
  
  return (
    <div className={styles.page}>
      {/* Header Navigation */}
      <HeaderNav
        onBack={handleBack}
        onShare={handleShare}
        onToggleSave={handleToggleSave}
        isSaved={isSaved}
      />
      
      {/* Hero Image Gallery */}
      <ImageGallery
        images={venue.images}
        venueName={venue.name}
        onViewAll={handleViewAllPhotos}
      />
      
      {/* Main Content */}
      <Container size="lg" padding className={styles.content}>
        <Stack direction="column" gap={6}>
          
          {/* Venue Header */}
          <section className={`${styles.section} ${styles.fadeIn}`}>
            <Stack direction="column" gap={3}>
              {/* Name and Status */}
              <Stack direction="row" gap={2} align="flex-start" justify="space-between">
                <Heading level={1} size="xl" color="primary" className={styles.venueName}>
                  {venue.name}
                </Heading>
                <div className={`${styles.statusBadge} ${isOpen ? styles.open : styles.closed}`}>
                  <span className={styles.statusDot} />
                  <Text size="sm" weight="semibold">
                    {isOpen ? 'Open' : 'Closed'}
                  </Text>
                </div>
              </Stack>
              
              {/* Rating and Hours */}
              <Stack direction="row" gap={4} align="center" wrap>
                <div className={styles.rating}>
                  <span className={styles.star}>★</span>
                  <Text weight="semibold" color="primary">{venue.rating}</Text>
                  <Text size="sm" color="muted">({venue.reviewCount} reviews)</Text>
                </div>
                <Text size="sm" color="secondary">
                  {venue.hours}
                </Text>
              </Stack>
              
              {/* Sport Badges */}
              <Stack direction="row" gap={2} wrap className={styles.sportBadges}>
                {venue.sports.map((sport) => (
                  <span key={sport.name} className={styles.sportBadge}>
                    {sport.name}
                  </span>
                ))}
              </Stack>
            </Stack>
          </section>
          
          {/* Quick Actions */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay1}`}>
            <QuickActions
              phone={venue.phone}
              website={venue.website}
              coordinates={venue.coordinates}
              onCall={handleCall}
              onDirections={handleDirections}
              onWebsite={handleWebsite}
              onSave={handleToggleSave}
              isSaved={isSaved}
            />
          </section>
          
          <Divider color="default" />
          
          {/* About Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay2}`}>
            <Stack direction="column" gap={3}>
              <Heading level={2} size="lg" color="primary">About</Heading>
              <div className={styles.descriptionContainer}>
                <Text 
                  color="secondary" 
                  leading="relaxed"
                  className={isDescriptionExpanded ? '' : styles.descriptionTruncated}
                >
                  {venue.description}
                </Text>
                {venue.description.length > 150 && (
                  <button
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    className={styles.readMoreButton}
                  >
                    {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                  </button>
                )}
              </div>
            </Stack>
          </section>
          
          {/* Amenities Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay3}`}>
            <Stack direction="column" gap={4}>
              <Heading level={2} size="lg" color="primary">Amenities</Heading>
              <AmenityList amenities={venue.amenities} />
            </Stack>
          </section>
          
          {/* Sports Available Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay4}`}>
            <Stack direction="column" gap={4}>
              <Heading level={2} size="lg" color="primary">Sports Available</Heading>
              <div className={styles.sportsGrid}>
                {venue.sports.map((sport) => (
                  <div key={sport.name} className={styles.sportCard}>
                    <div className={styles.sportIcon}>
                      {getSportIcon(sport.name)}
                    </div>
                    <div className={styles.sportInfo}>
                      <Text weight="semibold" color="primary">{sport.name}</Text>
                      <Text size="sm" color="muted">
                        {sport.courts} {sport.courts === 1 ? 'court' : 'courts'}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </Stack>
          </section>
          
          {/* Location Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay5}`}>
            <Stack direction="column" gap={4}>
              <Heading level={2} size="lg" color="primary">Location</Heading>
              <Text color="secondary">{venue.address}</Text>
              <div className={styles.mapPlaceholder}>
                <div className={styles.mapOverlay}>
                  <Text color="secondary">Map View</Text>
                  <button 
                    onClick={handleDirections}
                    className={styles.directionsButton}
                  >
                    Get Directions
                  </button>
                </div>
              </div>
            </Stack>
          </section>
          
          {/* Upcoming Events Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay6}`}>
            <Stack direction="column" gap={4}>
              <Stack direction="row" justify="space-between" align="center">
                <Heading level={2} size="lg" color="primary">Upcoming Events Here</Heading>
                <button 
                  onClick={handleViewAllEvents}
                  className={styles.viewAllLink}
                >
                  View All Events →
                </button>
              </Stack>
              <EventList 
                events={venue.upcomingEvents}
                onEventClick={handleEventClick}
              />
            </Stack>
          </section>
          
          {/* Reviews Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay7}`}>
            <Stack direction="column" gap={4}>
              <Stack direction="row" justify="space-between" align="center" wrap>
                <Heading level={2} size="lg" color="primary">
                  Reviews ({venue.rating})
                </Heading>
                <button className={styles.writeReviewButton}>
                  Write a Review
                </button>
              </Stack>
              
              {/* Rating Breakdown */}
              <div className={styles.ratingBreakdown}>
                {[5, 4, 3, 2, 1].map((stars) => (
                  <div key={stars} className={styles.ratingBar}>
                    <Text size="sm" color="muted">{stars}★</Text>
                    <div className={styles.ratingBarTrack}>
                      <div 
                        className={styles.ratingBarFill}
                        style={{ 
                          width: `${getRatingPercentage(venue.reviews, stars)}%` 
                        }}
                      />
                    </div>
                    <Text size="sm" color="muted">
                      {venue.reviews.filter(r => r.rating === stars).length}
                    </Text>
                  </div>
                ))}
              </div>
              
              {/* Featured Reviews */}
              <Stack direction="column" gap={3}>
                {venue.reviews.slice(0, 3).map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </Stack>
              
              {venue.reviews.length > 3 && (
                <button className={styles.seeAllReviewsLink}>
                  See All {venue.reviewCount} Reviews
                </button>
              )}
            </Stack>
          </section>
          
          {/* Similar Venues Section */}
          <section className={`${styles.section} ${styles.fadeIn} ${styles.delay8}`}>
            <SimilarVenues currentVenueId={venue.id} />
          </section>
          
        </Stack>
      </Container>
    </div>
  );
}

/**
 * Get sport icon character based on sport name
 */
function getSportIcon(sportName) {
  const icons = {
    'Basketball': '🏀',
    'Volleyball': '🏐',
    'Badminton': '🏸',
    'Tennis': '🎾',
    'Soccer': '⚽',
    'Football': '🏈',
    'Baseball': '⚾',
    'Swimming': '🏊',
    'Running': '🏃',
    'Cycling': '🚴',
    'Yoga': '🧘',
    'Golf': '⛳',
    'Hockey': '🏒',
    'Table Tennis': '🏓',
  };
  return icons[sportName] || '🏆';
}

/**
 * Calculate percentage for rating breakdown
 */
function getRatingPercentage(reviews, stars) {
  const count = reviews.filter(r => r.rating === stars).length;
  return reviews.length > 0 ? (count / reviews.length) * 100 : 0;
}

export default VenueDetailPage;
